import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import Loader from 'src/components/common/loader';
import Button from 'src/components/ui/button';

import catalogStore from 'src/stores/catalog-store';

import useCombinedRefs from 'src/hooks/use-combined-refs';
import {
  ApiRoute,
  CategoriesLength,
  IS_LOGO_TEXT_SEPARATED,
  WITH_CUSTOM_LOGO_HEIGHT,
  WITH_PHONE,
} from 'src/constants';
import type { CategoryButtonsProps } from './category-buttons.props';

import './category-buttons.scss';

const CategoryButtons = observer(
  forwardRef<HTMLDivElement, CategoryButtonsProps>(({ scrollToCategory, isSticky }, ref) => {
    const buttonsRef = useRef<HTMLDivElement | null>(null);
    const combinedRefs = useCombinedRefs<HTMLDivElement>(ref, buttonsRef);
    const [wrapperWidth, setWrapperWidth] = useState<number | null>(null);

    const {
      loadingState: { [ApiRoute.CATEGORIES]: isLoading },
      categoriesWithItems,
      activeCategory,
    } = catalogStore;

    const categoriesLength = Object.keys(categoriesWithItems).length;

    const categoryButtonsClasses = classNames('category-buttons', {
      'category-buttons_sticky': isSticky,
      'category-buttons_sticky_with-phone': isSticky && WITH_PHONE,
      'category-buttons_sticky_custom-logo-height':
        isSticky && (WITH_CUSTOM_LOGO_HEIGHT || IS_LOGO_TEXT_SEPARATED),
    });

    const wrapperClasses = classNames('category-buttons__wrapper', {
      'category-buttons__wrapper_scroll': categoriesLength > CategoriesLength.MAX_MOBILE_LENGTH,
    });

    const handleButtonClick = (category: string) => () => {
      scrollToCategory(category);
    };

    const handleResizeButtonsWrapper = useCallback(() => {
      if (buttonsRef.current && buttonsRef.current.parentElement && isSticky) {
        setWrapperWidth(buttonsRef.current.parentElement.getBoundingClientRect().width);
      } else if (!isSticky) {
        setWrapperWidth(null);
      }
    }, [isSticky]);

    useEffect(() => {
      handleResizeButtonsWrapper();

      window.addEventListener('resize', handleResizeButtonsWrapper);

      return () => {
        window.removeEventListener('resize', handleResizeButtonsWrapper);
      };
    }, [handleResizeButtonsWrapper]);

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className={categoryButtonsClasses} ref={combinedRefs}>
        <div className={wrapperClasses} style={{ maxWidth: wrapperWidth ?? '100%' }}>
          {categoriesWithItems.map(({ category, title }) => (
            <Button
              className="category-buttons__button"
              key={category}
              label={title}
              onClick={handleButtonClick(category)}
              size="mini"
              theme={activeCategory === category ? 'filled' : 'ghost'}
            />
          ))}
        </div>
      </div>
    );
  })
);

export default CategoryButtons;
